import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { firstValueFrom, Observable } from "rxjs";
import { environment } from "src/environment/environment";
import { ContractsService } from "./contracts.service";
import { CasesService } from "../../cases/_services/cases.service";
import { MessageService } from "primeng/api";

@Injectable({
    providedIn: 'root',
})

export class MultiService {

  private endpoint:string = 'https://web.multiasistencia.com/w3multi/';

  /** Nombre fijo de la ventana de Multiasistencia, reutilizado por ambos POST. */
  private readonly nombreVentana:string = 'multiWindow';

  /** Margen de respaldo (ms) por si el evento load no se dispara (cross-origin). */
  private readonly timeoutRepasos:number = 1500;

  constructor(
    private router:Router,
    private http:HttpClient,
    private caseService:CasesService,
    private contractService:ContractsService,
    private messageService:MessageService
  ) {

  }

  /**
   * Abre una ventana haciendo POST a `acceso.php` y, una vez cargada,
   * lanza un segundo POST a `repasos1.php` en la misma ventana.
   *
   * Como el destino es de otro origen (cross-origin), el evento `load` del
   * popup puede no dispararse; por eso se añade un timeout de respaldo y un
   * flag para garantizar que `repasos1.php` solo se envíe una vez.
   *
   * @param accesoData  Pares clave→valor enviados como POST a `acceso.php`.
   * @param repasosData Pares clave→valor enviados como POST a `repasos1.php`.
   * @returns La referencia al `Window` abierto, o `null` si el navegador lo bloquea.
   */
  async openCase(caseId:string, serviceNumber:string): Promise<Window | null> {

    //obtenemos expediente
    let _case = await firstValueFrom(this.caseService.get(caseId));

    //obtenemos contrato
    let contract = await firstValueFrom(this.contractService.get(_case.contractId));
    if(contract==null){
      this.messageService.add({ closable: true, severity: 'error', summary: 'Error', detail: 'Contrato no encontrado' });
      return null;
    }

    //obtenemos configuracion
    if(contract.integrationConfig==null || contract.integrationConfig==''){
      this.messageService.add({ closable: true, severity: 'error', summary: 'Error', detail: 'Contrato no tiene configuración de integración' });
      return null;
    }
    let integrationConfig = JSON.parse(contract.integrationConfig);
    let username = integrationConfig.username;
    let password = integrationConfig.password;
    
    const accesoData: Record<string, string> = {
      usuario: username,
      clave: password,
    };
    const repasosData: Record<string, string> = {
      reparacion: serviceNumber,
      prev_nav: '',
      desdebusqueda: '1',
    };

    const popup = window.open('', this.nombreVentana);
    if (!popup) {
      return null;
    }

    let repasosLanzado = false;
    const lanzarRepasos = () => {
      if (repasosLanzado) {
        return;
      }
      repasosLanzado = true;
      this.enviarFormPost(this.endpoint + 'repasos1.php', repasosData, this.nombreVentana);
    };

    // Intento principal: evento load del popup (poco fiable en cross-origin).
    popup.onload = lanzarRepasos;
    // Respaldo: si el load no llega, lanzamos repasos tras el timeout.
    setTimeout(lanzarRepasos, this.timeoutRepasos);

    // POST inicial a acceso.php, dirigido a la ventana recién abierta.
    this.enviarFormPost(this.endpoint + 'acceso.php', accesoData, this.nombreVentana);

    return popup;
  }

  /**
   * Crea un formulario oculto, lo envía por POST hacia `target` y lo elimina.
   */
  private enviarFormPost(action: string, datos: Record<string, string>, target: string): void {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = action;
    form.target = target;
    form.enctype = 'application/x-www-form-urlencoded';
    form.style.display = 'none';

    Object.entries(datos).forEach(([clave, valor]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = clave;
      input.value = valor;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }

}
